<!-- ══════════════════════════════════════════════════════════
     SUBSCRIPTION CONTEXT BANNER
══════════════════════════════════════════════════════════ -->
<div *ngIf="subscription" class="ul-subscription-banner mb-3">
  <div class="d-flex align-items-center flex-wrap gap-3">
    <div class="d-flex align-items-center gap-2">
      <i class="fa fa-id-card-o text-primary fs-5"></i>
      <span class="fw-semibold text-dark">Plan #{{ subscription.planId }}</span>
      <span class="ul-status-badge"
        [class.ul-status-active]="subscription.status === 'Active'"
        [class.ul-status-inactive]="subscription.status !== 'Active'">
        {{ subscription.status }}
      </span>
    </div>
    <div class="vr d-none d-sm-block text-muted opacity-25"></div>
    <div class="d-flex align-items-center gap-1 text-muted small">
      <i class="fa fa-calendar-o me-1"></i>
      {{ subscription.startDate | date:'dd MMM yyyy' }} → {{ subscription.endDate | date:'dd MMM yyyy' }}
    </div>
    <div class="ms-auto d-flex gap-2">
      <button class="btn btn-sm btn-primary" (click)="openAssignModal(assignModal)">
        <i class="fa fa-plus me-1"></i> Assign User
      </button>
      <button class="btn btn-sm btn-outline-secondary" (click)="loadLicenses()" title="Refresh">
        <i class="fa fa-refresh"></i>
      </button>
    </div>
  </div>
</div>

<!-- ══════════════════════════════════════════════════════════
     STAT CARDS
══════════════════════════════════════════════════════════ -->
<div class="row g-3 mb-3">
  <!-- Included Users -->
  <div class="col-6 col-md-3">
    <div class="ul-stat-card ul-card-total">
      <div class="ul-stat-icon"><i class="fa fa-check-circle"></i></div>
      <div class="ul-stat-value">
        {{ occupiedIncluded }}
        <span class="ul-stat-denom">/ {{ maxIncluded }}</span>
      </div>
      <div class="ul-stat-label">Included Users</div>
      <div class="ul-stat-sub">{{ availableIncluded }} available</div>
    </div>
  </div>
  <!-- Premium Users -->
  <div class="col-6 col-md-3">
    <div class="ul-stat-card ul-card-occupied">
      <div class="ul-stat-icon"><i class="fa fa-star"></i></div>
      <div class="ul-stat-value">
        {{ occupiedPremium }}
        <span class="ul-stat-denom">/ {{ maxPremium }}</span>
      </div>
      <div class="ul-stat-label">Premium Users</div>
      <div class="ul-stat-sub">{{ availablePremium }} available</div>
    </div>
  </div>
  <!-- Free Users -->
  <div class="col-6 col-md-3">
    <div class="ul-stat-card ul-card-available">
      <div class="ul-stat-icon"><i class="fa fa-gift"></i></div>
      <div class="ul-stat-value">
        {{ summaryData?.freeOccupied ?? 0 }}
        <span class="ul-stat-denom">/ 9999</span>
      </div>
      <div class="ul-stat-label">Free Users</div>
      <div class="ul-stat-sub">Unlimited capacity</div>
    </div>
  </div>
  <!-- Total -->
  <div class="col-6 col-md-3">
    <div class="ul-stat-card ul-card-plan">
      <div class="ul-stat-icon"><i class="fa fa-th-large"></i></div>
      <div class="ul-stat-value">
        {{ occupiedCount }}
        <span class="ul-stat-denom">/ {{ totalPlanLimit }}</span>
      </div>
      <div class="ul-stat-label">Total Occupied</div>
      <div class="ul-stat-sub" *ngIf="summaryData">
        {{ summaryData.includedPlanLimit }} + ({{ summaryData.extraUserBlocks }} × {{ summaryData.extraUserBlockSize }}) = {{ totalPlanLimit }} capacity
      </div>
    </div>
  </div>
</div>

<!-- ══════════════════════════════════════════════════════════
     CHARTS ROW
══════════════════════════════════════════════════════════ -->
<div *ngIf="!isDataLoading && licenseItems.length > 0" class="row g-3 mb-3">
  <div class="col-12 col-md-5">
    <div class="ul-chart-card">
      <div class="ul-chart-title">
        <i class="fa fa-pie-chart me-2 text-primary"></i>Slot Usage
      </div>
      <div id="ul-donut-chart" class="ul-chart-donut"></div>
    </div>
  </div>
  <div class="col-12 col-md-7">
    <div class="ul-chart-card">
      <div class="ul-chart-title">
        <i class="fa fa-bar-chart me-2 text-primary"></i>Usage by License Type
      </div>
      <div id="ul-bar-chart" class="ul-chart-bar"></div>
    </div>
  </div>
</div>

<!-- ══════════════════════════════════════════════════════════
     LOADING
══════════════════════════════════════════════════════════ -->
<div *ngIf="isDataLoading" class="text-center py-4">
  <div class="spinner-border text-primary" role="status"></div>
  <div class="mt-2 text-muted small">Loading license slots…</div>
</div>

<!-- ══════════════════════════════════════════════════════════
     LICENSE TABLE
══════════════════════════════════════════════════════════ -->
<div *ngIf="!isDataLoading" class="ul-table-card">
  <div class="ul-table-header">
    <div class="ul-table-title">
      <i class="fa fa-list-ul me-2 text-primary"></i>License Slots
      <span class="ul-slot-count ms-2">{{ licenseItems.length }}</span>
    </div>
    <div *ngIf="!subscription" class="ms-auto d-flex gap-2">
      <button class="btn btn-sm btn-primary" (click)="openAssignModal(assignModal)">
        <i class="fa fa-plus me-1"></i> Assign User
      </button>
      <button class="btn btn-sm btn-outline-secondary" (click)="loadLicenses()">
        <i class="fa fa-refresh"></i>
      </button>
    </div>
  </div>

  <div class="table-responsive">
    <table class="table table-hover ul-table mb-0">
      <thead>
        <tr>
          <th style="width:40px">#</th>
          <th>Type</th>
          <th>User</th>
          <th class="text-center">Billable</th>
          <th>Valid From</th>
          <th>Valid To</th>
          <th>Assigned By</th>
          <th class="text-center">Status</th>
          <th class="text-center">Action</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let item of licenseItems; let i = index"
            [class.ul-row-occupied]="!!item.userId"
            [class.ul-row-available]="!item.userId">
          <td class="text-center text-muted small">{{ i + 1 }}</td>
          <td>
            <span class="ul-type-badge"
              [class.ul-type-included]="item.licenseType === 'INCLUDED'"
              [class.ul-type-premium]="item.licenseType === 'PREMIUM'"
              [class.ul-type-free]="item.licenseType === 'FREE'">
              <i class="fa me-1"
                [class.fa-check-circle]="item.licenseType === 'INCLUDED'"
                [class.fa-star]="item.licenseType === 'PREMIUM'"
                [class.fa-gift]="item.licenseType === 'FREE'"></i>
              {{ item.licenseType }}
            </span>
          </td>
          <td>
            <div *ngIf="item.userName" class="d-flex align-items-center gap-2">
              <div class="ul-avatar" [style.background]="getUserColor(item.userName)">
                {{ item.userName.charAt(0).toUpperCase() }}
              </div>
              <span class="small fw-medium">{{ item.userName }}</span>
            </div>
            <span *ngIf="!item.userName" class="text-muted small fst-italic">— Available —</span>
          </td>
          <td class="text-center">
            <span class="ul-dot"
                  [class.ul-dot-yes]="item.isBillable"
                  [class.ul-dot-no]="!item.isBillable"
                  [title]="item.isBillable ? 'Billable' : 'Non-billable'">
              <i class="fa" [class.fa-circle]="item.isBillable" [class.fa-circle-o]="!item.isBillable"></i>
            </span>
          </td>
          <td class="small">{{ item.validFrom ? (item.validFrom | date:'dd MMM yyyy') : '—' }}</td>
          <td class="small">{{ item.validTo   ? (item.validTo   | date:'dd MMM yyyy') : '—' }}</td>
          <td class="small text-muted">{{ item.assignedBy ?? '—' }}</td>
          <td class="text-center">
            <span class="ul-status-chip"
                  [class.ul-chip-occupied]="!!item.userId"
                  [class.ul-chip-free]="!item.userId">
              {{ item.userId ? 'Occupied' : 'Free' }}
            </span>
          </td>
          <td class="text-center">
            <button class="ul-btn-delete" title="Delete slot" (click)="deleteSlot(item)">
              <i class="fa fa-trash-o"></i>
            </button>
          </td>
        </tr>
        <tr *ngIf="licenseItems.length === 0">
          <td colspan="9" class="text-center py-4">
            <i class="fa fa-inbox fa-2x text-muted mb-2 d-block"></i>
            <span class="text-muted">No license slots found. Click <strong>Assign User</strong> to get started.</span>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</div>

<!-- ══════════════════════════════════════════════════════════
     ASSIGN MODAL
══════════════════════════════════════════════════════════ -->
<ng-template #assignModal let-modal>
  <div class="modal-header border-0 pb-0">
    <div>
      <h5 class="modal-title">
        <i class="fa fa-user-plus me-2 text-primary"></i>
        {{ editingLicenseId ? 'Edit License Slot' : 'Assign User to License Slot' }}
      </h5>
      <p class="text-muted small mb-0">Tenant ID: {{ tenantId }}</p>
    </div>
    <button type="button" class="btn-close" (click)="modal.dismiss()"></button>
  </div>
  <div class="modal-body pt-2">
    <div *ngIf="assignErrorMsg" class="alert alert-danger py-2 small">
      <i class="fa fa-exclamation-circle me-1"></i>{{ assignErrorMsg }}
    </div>
    <div *ngIf="maxIncluded > 0" class="ul-usage-hint mb-3"
         [class.ul-hint-warn]="availableIncluded === 0"
         [class.ul-hint-info]="availableIncluded > 0">
      <i class="fa fa-info-circle me-1"></i>
      Included slots: <strong>{{ occupiedIncluded }} / {{ maxIncluded }}</strong> used.
      <span *ngIf="availableIncluded === 0"> All included slots occupied — select Premium or Free.</span>
    </div>
    <form [formGroup]="assignForm">
      <div class="mb-3">
        <label class="form-label fw-medium">License Type <span class="text-danger">*</span></label>
        <select class="form-select form-select-sm" formControlName="licenseType">
          <option *ngFor="let t of licenseTypeOptions"
                  [value]="t.value"
                  [disabled]="t.limited && t.available === 0">
            {{ t.label }}{{ t.limited ? ' (' + t.available + ' available)' : '' }}
          </option>
        </select>
      </div>
      <div class="mb-3">
        <label class="form-label fw-medium">Select User <span class="text-danger">*</span></label>
        <select class="form-select form-select-sm" formControlName="userId">
          <option [ngValue]="null" disabled>— Select a user —</option>
          <option *ngFor="let u of userList" [ngValue]="+u.key">{{ u.value }}</option>
        </select>
        <div *ngIf="userList.length === 0" class="form-text text-muted">No users found.</div>
      </div>
      <div class="row g-2">
        <div class="col-6">
          <label class="form-label fw-medium">Valid From</label>
          <input type="date" class="form-control form-control-sm" formControlName="validFrom" />
        </div>
        <div class="col-6">
          <label class="form-label fw-medium">Valid To</label>
          <input type="date" class="form-control form-control-sm" formControlName="validTo" />
        </div>
      </div>
    </form>
  </div>
  <div class="modal-footer border-0 pt-0">
    <button class="btn btn-sm btn-outline-secondary" (click)="modal.dismiss()">Cancel</button>
    <button class="btn btn-sm btn-primary" (click)="confirmAssign()">
      <i class="fa fa-check me-1"></i> Assign
    </button>
  </div>
</ng-template>

